import type { ApiError } from "@restomenu/core/client";

/**
 * Пути домена заказов в кэше TanStack.
 *
 * Список нужен инвалидации: анонимная сессия, поднятая заново, имеет НОВЫЙ
 * идентификатор пользователя, и заказы прежней сессии обязаны протухнуть —
 * `scope=own` вернёт уже другую выборку.
 */
export const ORDER_PATHS = ["/orders", "/orders/{id}"] as const;

/** «Заказа нет ЛИБО он чужой» — ответы неразличимы намеренно. */
export const ORDER_NOT_FOUND_CODE = 1329;

/**
 * Доменные коды выбора опций. Подписи сверены с описанием маршрутов
 * POST /orders и PATCH /orders/{id} в схеме, а не написаны по памяти:
 * Ф7 уже ловила расхождение справочника со схемой (код 1329).
 */
export const OPTION_NOT_FOUND_CODE = 13220;
export const OPTION_UNAVAILABLE_CODE = 13221;
export const OPTION_BLOCK_LIMIT_CODE = 13222;
export const OPTION_QUANTITY_LIMIT_CODE = 13223;

/**
 * Коды, пришедшие с обновлением контракта 2026-09-08 (core 6827908).
 *
 * 13224 виджету НЕдостижим: тело правки собирает единственное место
 * (useCartSync), а diffToPatch возвращает ровно три ключа дельты, и `items`
 * среди них нет. Подпись держим всё равно — недостижимость обеспечена
 * устройством одной функции, и её поломка не должна выходить к гостю сырым
 * сообщением бэка. Само свойство закреплено contract-invariants.test.ts.
 *
 * 13225 достижим: клиентская проверка потолка закрывает только 400, а
 * считает ли домен итог брутто или нетто — из схемы не выводится (долг
 * задачи, вопрос к бэку).
 *
 * `path` у обоих отличается от привычного 13222: у 13224 он указывает на
 * `items`, у 13225 — на идентификатор заказа. Разбора blockIdFromErrorPath
 * это не касается: там гард по коду.
 */
export const ITEMS_MUTATION_CONFLICT_CODE = 13224;
export const ITEMS_QUANTITY_LIMIT_CODE = 13225;

/**
 * Потолок количества из контракта (2026-09-08).
 *
 * Схема различает списки: у элемента `itemsAdd` диапазон 1..1000, у `items`
 * и `itemsUpdate` — 0..1000. Ноль у виджета означает снятие позиции и до
 * `itemsUpdate` не доходит (diffToPatch выражает снятие через itemsRemove),
 * поэтому верхняя граница у обоих путей одна и константа одна.
 *
 * Тем же числом ограничена СУММА `quantity` по каждому списку, и схемой это
 * не выражается: `maximum` ограничивает одно число, а домен заводит строку
 * на каждую единицу количества.
 */
export const MAX_CART_QUANTITY = 1000;

/**
 * Идентификатор нарушенного блока из 13222.
 *
 * Схема обещает его ПОСЛЕДНИМ сегментом path и по ошибке на каждый
 * нарушенный блок — поэтому несколько блоков различимы, и подсветить можно
 * все сразу.
 *
 * Тип параметра — Pick от ApiError (ревью задачи 9), а не ручной дубль двух
 * полей: дрейф схемы по code/path компилятор ловит здесь же, а не молчит,
 * пока форма руками написанного типа расходится с генерируемой. Круг 9 это
 * уже подтвердил: `code` стал обязательным, и Pick подхватил обязательность
 * сам, без правки здесь. Необязательным остался `path` — по нему и сверяем.
 */
export function blockIdFromErrorPath(
  error: Pick<ApiError, "code" | "path">,
): string | null {
  if (error.code !== OPTION_BLOCK_LIMIT_CODE) return null;
  const last = error.path?.[error.path.length - 1];
  return last ?? null;
}
